import {
  Button,
  Countdown,
  ExternalPlatformIcon,
  getExternalPlatformLabel,
  Text,
  Tooltip,
} from '@shared/components';
import { Avatar } from '@shared/components/avatar/Avatar';
import { DocRenderer } from '@shared/components/doc-viewer/DocViewer';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { ActionExecutionEventComponent } from '@shared/features/action-execution-event/ActionExecutionEvent';
import { ChatAttachments } from '@shared/features/conversation-transcript/components/transcript/ChatAttachments';
import { mayShowAEEvent } from '@shared/features/conversation-transcript/components/transcript/Transcript';
import { useConversationMessages } from '@shared/features/conversation-transcript/components/transcript/useConversationMessages';
import {
  InteractiveCard,
  QuickReplyButtons,
} from '@shared/features/interactive-card';
import {
  ActionExecutionEventType,
  MessageRatingFragment,
} from '@shared/generated/graphql';
import { useInterval } from '@shared/hooks/useInterval';
import { usePlatform } from '@shared/hooks/usePlatform';
import {
  DEFAULT_CHAT_VIS_SETTINGS,
  useTaskVisSettings,
} from '@shared/hooks/useTaskVisSettings';
import { formatFullDatetime } from '@shared/plugin/utils/datetime';
import {
  ActionExecutionEvent,
  isRunningToolCall,
} from '@shared/types/action-execution';
import {
  ChatMessageEvent,
  ConversationContributionRole,
  ConversationMessageEvent,
  ConversationMessageEventType,
  ExternalPlatform,
  MessageAttachment,
} from '@shared/types/conversation';
import { toName, User } from '@shared/types/user';
import { makeRootClassName } from '@shared/utils';
import { isQuickReplyCard } from 'clerk_common/cards/quickReply';
import { AGENT_UNAVAILABLE_MESSAGE } from 'clerk_common/constants';
import {
  isMirroredToolCallTranscript,
  isReplyToConversation,
} from 'clerk_common/stringification/toolCalls';
import { findLast, isNil, last, sumBy } from 'lodash';
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { PiWarningCircle } from 'react-icons/pi';
import { useIsomorphicLayoutEffect } from 'usehooks-ts';
import { useChatAutoScrollCountdown } from '../hooks/useChatAutoScrollCountdown';
import { useMarkConversationRead } from '../hooks/useMarkConversationRead';
import { useMessageRatings } from '../hooks/useMessageRatings';
import {
  formatChatMessageTimestamp,
  getLocalDayKey,
  shouldCollapseChatTimestamp,
} from '../utils/chatTimestamps';
import { convertSlackMrkdwnLinks } from '../utils/convertSlackMrkdwnLinks';
import { ChatDayLabel } from './ChatDayLabel';
import { MessageFeedback } from './message-feedback/MessageFeedback';
import { MessageMarkdown } from './MessageMarkdown';
import { OptimisticChatMessage } from './optimisticChatMessage';
import { ToolCallGroup, ToolCallShimmer } from './ToolCallGroup';
import { getToolCallGroupKey, UserToolCallEvent } from './UserToolCallEvent';

type EventItem = {
  kind: 'event';
  event: ActionExecutionEvent;
  repeatCount: number;
  runningCount: number;
};

type TranscriptItem =
  | {
      kind: 'message';
      message: ChatMessageEvent;
      sourcePlatform?: ExternalPlatform;
      mirroredPlatforms: ExternalPlatform[];
    }
  | EventItem
  | { kind: 'tool-group'; items: EventItem[] }
  | { kind: 'optimistic-message'; message: OptimisticChatMessage };

type ChatTranscriptProps = {
  conversationId: string;
  actionExecutionEvents: ActionExecutionEvent[];
  countdownSlot?: HTMLElement | null;
  isAgentWorking?: boolean;
  onAwaitingReplyChange?: (isAwaitingReply: boolean) => void;
  onExternalChannelAvailabilityChange?: (isAvailable: boolean) => void;
  onOptimisticMessagePersisted?: (messageId: string) => void;
  optimisticMessages?: OptimisticChatMessage[];
  scrollContainer?: HTMLElement | null;
};

const EMPTY_OPTIMISTIC_MESSAGES: OptimisticChatMessage[] = [];
const MIRRORABLE_EXTERNAL_PLATFORMS = new Set([
  ExternalPlatform.ASSISTANT_EMAIL,
  ExternalPlatform.MICROSOFT_TEAMS,
  ExternalPlatform.SLACK,
]);
const NOOP_AWAITING_REPLY_CHANGE = () => undefined;
const NOOP_EXTERNAL_CHANNEL_CHANGE = () => undefined;
const NOOP_OPTIMISTIC_MESSAGE_PERSISTED = () => undefined;
export const ACTIVE_TURN_POLL_INTERVAL_MS = 2 * 1000;
const AGENT_TURN_START_TIMEOUT_MS = 2 * 60 * 1000;
const AWAITING_REPLY_TICK_MS = 5 * 1000;
const UNAVAILABLE_NOTICE_ROOT = makeRootClassName('AgentUnavailableNotice');
const CHAT_FALLBACK_POLL_INTERVAL_MS = 60 * 1000;
const OPTIMISTIC_MESSAGE_MATCH_WINDOW_MS = 30 * 1000;

function renderDocumentPreview(attachment: MessageAttachment) {
  return <DocRenderer url={attachment.url} fileName={attachment.name} />;
}

export function ChatTranscript({
  conversationId,
  actionExecutionEvents,
  countdownSlot = null,
  isAgentWorking = false,
  onAwaitingReplyChange = NOOP_AWAITING_REPLY_CHANGE,
  onExternalChannelAvailabilityChange = NOOP_EXTERNAL_CHANNEL_CHANGE,
  onOptimisticMessagePersisted = NOOP_OPTIMISTIC_MESSAGE_PERSISTED,
  optimisticMessages = EMPTY_OPTIMISTIC_MESSAGES,
  scrollContainer = null,
}: ChatTranscriptProps) {
  const { isVoomaAdmin, me } = useMeContext();
  const { shouldShow } = useTaskVisSettings(DEFAULT_CHAT_VIS_SETTINGS);
  const usesCompactToolCalls =
    !isVoomaAdmin || !shouldShow(ActionExecutionEventType.TOOL_USED);
  const { msgs, isHistoryStale } = useConversationMessages(conversationId, {
    includeMirrored: true,
    pollIntervalMs: isAgentWorking
      ? ACTIVE_TURN_POLL_INTERVAL_MS
      : CHAT_FALLBACK_POLL_INTERVAL_MS,
  });
  useMarkConversationRead(conversationId, getLatestMessageId(msgs));

  const items = useMemo(
    () =>
      combineItems(
        msgs,
        actionExecutionEvents,
        optimisticMessages,
        conversationId,
        isVoomaAdmin,
        shouldShow,
        usesCompactToolCalls
      ),
    [
      msgs,
      actionExecutionEvents,
      optimisticMessages,
      conversationId,
      isVoomaAdmin,
      shouldShow,
      usesCompactToolCalls,
    ]
  );
  const veeReplyIds = useMemo(() => items.flatMap(toVeeReplyId), [items]);
  const { ratingsByMessageId, hasLoaded: haveRatingsLoaded } =
    useMessageRatings(veeReplyIds);
  const latestVeeReplyId = last(veeReplyIds);
  const confirmedLocalMessageIdsRef = useRef(new Set<string>());
  const confirmedLocalMessageConversationIdRef = useRef(conversationId);
  if (confirmedLocalMessageConversationIdRef.current !== conversationId) {
    confirmedLocalMessageConversationIdRef.current = conversationId;
    confirmedLocalMessageIdsRef.current = new Set();
  }
  const localActivityKey = optimisticMessages.map(({ id }) => id).join(',');
  const remoteActivityKey = useMemo(() => {
    const remoteMessageIds = msgs.flatMap((message) => {
      if (!isDisplayableChatMessage(message)) {
        return [];
      }
      if (confirmedLocalMessageIdsRef.current.has(message.id)) {
        return [];
      }
      const isLocallyOriginated = optimisticMessages.some(
        (optimistic) =>
          optimistic.persistedMessageId === message.id ||
          matchesOptimisticMessage(message, optimistic)
      );
      if (isLocallyOriginated) {
        confirmedLocalMessageIdsRef.current.add(message.id);
        return [];
      }
      return [message.id];
    });
    return [
      remoteMessageIds.join(','),
      actionExecutionEvents.map(({ id }) => id).join(','),
      String(isAgentWorking),
    ].join('|');
  }, [actionExecutionEvents, isAgentWorking, msgs, optimisticMessages]);
  const isLocalTurn = endsWithLocalTurn(
    items,
    confirmedLocalMessageIdsRef.current
  );
  const isAwaitingReply = isLocalTurn && isAwaitingAgentReply(items);
  const isTurnActive = isAgentWorking || isAwaitingReply;
  const [, setNow] = useState(Date.now);
  useInterval(
    () => setNow(Date.now()),
    isAwaitingReply ? AWAITING_REPLY_TICK_MS : null
  );
  const isReplyOverdue =
    (isLocalTurn || !isHistoryStale) &&
    !isAgentWorking &&
    isAgentReplyOverdue(items);
  const {
    bottomAnchorRef,
    countdownInstanceKey,
    dismissCountdown,
    isCountdownVisible,
    scrollToBottom,
  } = useChatAutoScrollCountdown({
    conversationId,
    localActivityKey,
    remoteActivityKey,
    scrollContainer,
  });
  const persistedOptimisticMessageIds = useMemo(() => {
    const persistedMessageIds = new Set(msgs.map(({ id }) => id));
    return optimisticMessages.flatMap(({ id, persistedMessageId }) =>
      persistedMessageId && persistedMessageIds.has(persistedMessageId)
        ? [id]
        : []
    );
  }, [msgs, optimisticMessages]);
  const hasMirrorableExternalChannel = msgs.some(
    (message) =>
      message.type === ConversationMessageEventType.CHAT_MESSAGE &&
      !isNil(message.externalPlatform) &&
      MIRRORABLE_EXTERNAL_PLATFORMS.has(message.externalPlatform)
  );

  useEffect(() => {
    persistedOptimisticMessageIds.forEach(onOptimisticMessagePersisted);
  }, [onOptimisticMessagePersisted, persistedOptimisticMessageIds]);

  useEffect(() => {
    onAwaitingReplyChange(isAwaitingReply);
  }, [isAwaitingReply, onAwaitingReplyChange]);

  useIsomorphicLayoutEffect(() => {
    onExternalChannelAvailabilityChange(hasMirrorableExternalChannel);
  }, [
    conversationId,
    hasMirrorableExternalChannel,
    onExternalChannelAvailabilityChange,
  ]);

  const renderItem = (item: TranscriptItem, itemIndex: number) => {
    if (item.kind === 'message') {
      return (
        <ChatMessage
          isTimestampCollapsed={shouldCollapseChatTimestamp(
            item.message.createdAt,
            getNextStampedCreatedAt(items, itemIndex)
          )}
          message={item.message}
          sourcePlatform={item.sourcePlatform}
          mirroredPlatforms={item.mirroredPlatforms}
          rating={ratingsByMessageId.get(item.message.id)}
          isLatestVeeReply={item.message.id === latestVeeReplyId}
          isRatingLoading={!haveRatingsLoaded}
        />
      );
    }
    if (item.kind === 'optimistic-message') {
      return (
        <OptimisticHumanMessage
          message={item.message}
          user={
            me
              ? {
                  id: me.id,
                  name: me.profile?.name ?? undefined,
                  roles: me.roles,
                  userName: me.username ?? undefined,
                }
              : undefined
          }
        />
      );
    }
    const isLive = isTurnActive && itemIndex === items.length - 1;
    if (item.kind === 'tool-group') {
      const liveCall = findLast(item.items, hasRunningCall) ?? last(item.items);
      return (
        <ToolCallGroup
          callCount={sumBy(
            item.items,
            (toolCall) => toolCall.repeatCount - toolCall.runningCount
          )}
          liveTitle={
            isLive &&
            liveCall && (
              <UserToolCallEvent
                event={liveCall.event}
                isRunning
                repeatCount={liveCall.repeatCount}
              />
            )
          }
        >
          {item.items.map((toolCall) => (
            <Fragment key={toolCall.event.id}>{renderEvent(toolCall)}</Fragment>
          ))}
        </ToolCallGroup>
      );
    }
    if (isLive && usesCompactToolCalls && isToolCallItem(item)) {
      return (
        <ToolCallShimmer>
          <UserToolCallEvent
            event={item.event}
            isRunning
            repeatCount={item.repeatCount}
          />
        </ToolCallShimmer>
      );
    }
    return renderEvent(item);
  };

  const renderEvent = (item: EventItem) => {
    if (usesCompactToolCalls && isToolCallItem(item)) {
      return (
        <UserToolCallEvent
          event={item.event}
          isRunning={hasRunningCall(item)}
          repeatCount={item.repeatCount}
        />
      );
    }
    return <ActionExecutionEventComponent event={item.event} />;
  };

  return (
    <div className="flex w-full flex-col">
      {isCountdownVisible &&
        countdownSlot &&
        createPortal(
          <div className="pointer-events-auto">
            <Countdown
              key={countdownInstanceKey}
              title="New activity"
              onBlur={dismissCountdown}
              onCancel={dismissCountdown}
              onClick={scrollToBottom}
              onTimeout={scrollToBottom}
            />
          </div>,
          countdownSlot
        )}
      {items.map((item, idx) => {
        const previous = items[idx - 1];
        const showDayChip = isNewLocalDay(item, previous);
        const spacingClassName = getTranscriptItemSpacing(
          item,
          previous,
          showDayChip
        );
        return (
          <Fragment key={getItemKey(item)}>
            {showDayChip && (
              <ChatDayLabel
                date={getItemCreatedAt(item)}
                hasPrecedingItems={Boolean(previous)}
              />
            )}
            <div className={spacingClassName}>{renderItem(item, idx)}</div>
          </Fragment>
        );
      })}
      {isTurnActive && !hasLiveToolCall(items) && <AgentWorkingIndicator />}
      {isReplyOverdue && <AgentUnavailableNotice />}
      <div ref={bottomAnchorRef} />
    </div>
  );
}

function getTranscriptItemSpacing(
  item: TranscriptItem,
  previous: TranscriptItem | undefined,
  showDayChip: boolean
): string {
  if (showDayChip) {
    return previous ? 'mt-2' : '';
  }
  return getItemSpacing(item, previous);
}

function getItemSpacing(
  item: TranscriptItem,
  previous?: TranscriptItem
): string {
  if (!previous) return '';
  const isSameRoleMessagePair =
    ((item.kind === 'message' &&
      item.message.role === ConversationContributionRole.USER) ||
      item.kind === 'optimistic-message') &&
    ((previous.kind === 'message' &&
      previous.message.role === ConversationContributionRole.USER) ||
      previous.kind === 'optimistic-message');
  return isSameRoleMessagePair ? 'mt-2' : 'mt-6';
}

function combineItems(
  messages: ConversationMessageEvent[],
  events: ActionExecutionEvent[],
  optimisticMessages: OptimisticChatMessage[],
  conversationId: string,
  isVoomaAdmin: boolean,
  shouldShow: (eventType: ActionExecutionEvent['name']) => boolean,
  usesCompactToolCalls: boolean
): TranscriptItem[] {
  const mirroredPlatformsByCanonicalId =
    buildMirroredPlatformsByCanonicalId(messages);
  const messageItems: TranscriptItem[] = messages
    .filter(isDisplayableChatMessage)
    .map((message) => {
      const { sourcePlatform, mirroredPlatforms } = getPlatformDirections(
        message,
        mirroredPlatformsByCanonicalId
      );
      return {
        kind: 'message',
        message,
        sourcePlatform,
        mirroredPlatforms,
      };
    });
  const eventItems: TranscriptItem[] = events
    .filter(
      (event) =>
        (event.name === ActionExecutionEventType.TOOL_USED ||
          shouldShow(event.name)) &&
        mayShowAEEvent(event.name, isVoomaAdmin) &&
        !isReplyToCurrentConversation(event, conversationId)
    )
    .map((event) => ({
      kind: 'event',
      event,
      repeatCount: 1,
      runningCount: isRunningToolCall(event.data) ? 1 : 0,
    }));
  const persistedMessageIds = new Set(messages.map(({ id }) => id));
  const claimedPersistedMessageIds = new Set(
    optimisticMessages.flatMap(({ persistedMessageId }) =>
      persistedMessageId ? [persistedMessageId] : []
    )
  );
  const unmatchedPersistedMessages = messages.filter(
    (message): message is ChatMessageEvent =>
      isDisplayableChatMessage(message) &&
      message.role === ConversationContributionRole.USER &&
      !claimedPersistedMessageIds.has(message.id)
  );
  const optimisticMessageItems: TranscriptItem[] = optimisticMessages.flatMap(
    (message) => {
      if (
        message.persistedMessageId &&
        persistedMessageIds.has(message.persistedMessageId)
      ) {
        return [];
      }
      if (!message.persistedMessageId) {
        const persistedMessageIndex = unmatchedPersistedMessages.findIndex(
          (persistedMessage) =>
            matchesOptimisticMessage(persistedMessage, message)
        );
        if (persistedMessageIndex >= 0) {
          unmatchedPersistedMessages.splice(persistedMessageIndex, 1);
          return [];
        }
      }
      return [{ kind: 'optimistic-message', message }];
    }
  );
  const sortedItems = [
    ...messageItems,
    ...eventItems,
    ...optimisticMessageItems,
  ].sort((a, b) => getItemTime(a) - getItemTime(b));
  const items = hideRepeatedPlatformsUntilLastMessage(sortedItems);
  return groupConsecutiveToolCalls(
    usesCompactToolCalls ? collapseRepeatedToolCalls(items) : items
  );
}

function endsWithLocalTurn(
  items: TranscriptItem[],
  localMessageIds: Set<string>
): boolean {
  const lastItem = last(items);
  if (lastItem?.kind === 'optimistic-message') return true;
  return (
    lastItem?.kind === 'message' && localMessageIds.has(lastItem.message.id)
  );
}

function isAwaitingAgentReply(items: TranscriptItem[]): boolean {
  const age = getUnansweredUserTurnAgeMs(items);
  return !isNil(age) && age < AGENT_TURN_START_TIMEOUT_MS;
}

function isAgentReplyOverdue(items: TranscriptItem[]): boolean {
  const age = getUnansweredUserTurnAgeMs(items);
  return !isNil(age) && age >= AGENT_TURN_START_TIMEOUT_MS;
}

function getUnansweredUserTurnAgeMs(items: TranscriptItem[]): number | null {
  const lastItem = last(items);
  if (!lastItem) return null;
  const isUserTurn =
    lastItem.kind === 'optimistic-message'
      ? lastItem.message.status !== 'failed'
      : lastItem.kind === 'message' &&
        lastItem.message.role === ConversationContributionRole.USER;
  return isUserTurn ? Date.now() - getItemTime(lastItem) : null;
}

function hasLiveToolCall(items: TranscriptItem[]): boolean {
  const lastItem = last(items);
  return (
    lastItem?.kind === 'tool-group' ||
    (lastItem !== undefined && isToolCallItem(lastItem))
  );
}

function hasRunningCall(item: EventItem): boolean {
  return item.runningCount > 0;
}

function isToolCallItem(item: TranscriptItem): item is EventItem {
  return (
    item.kind === 'event' &&
    item.event.name === ActionExecutionEventType.TOOL_USED
  );
}

function collapseRepeatedToolCalls(items: TranscriptItem[]): TranscriptItem[] {
  return items.reduce<TranscriptItem[]>((collapsed, item) => {
    const previous = collapsed[collapsed.length - 1];
    if (
      previous &&
      isToolCallItem(previous) &&
      isToolCallItem(item) &&
      getToolCallGroupKey(previous.event) === getToolCallGroupKey(item.event)
    ) {
      // NOTE(aman): Running repeats merge too, so the live line doesn't split
      // into a group and back while a call runs.
      collapsed[collapsed.length - 1] = {
        ...previous,
        repeatCount: previous.repeatCount + 1,
        runningCount: previous.runningCount + item.runningCount,
      };
    } else {
      collapsed.push(item);
    }
    return collapsed;
  }, []);
}

function groupConsecutiveToolCalls(items: TranscriptItem[]): TranscriptItem[] {
  return items.reduce<TranscriptItem[]>((grouped, item) => {
    const lastIndex = grouped.length - 1;
    const previous = grouped[lastIndex];
    if (!isToolCallItem(item) || !previous || isNewLocalDay(item, previous)) {
      grouped.push(item);
    } else if (previous.kind === 'tool-group') {
      grouped[lastIndex] = { ...previous, items: [...previous.items, item] };
    } else if (isToolCallItem(previous)) {
      grouped[lastIndex] = { kind: 'tool-group', items: [previous, item] };
    } else {
      grouped.push(item);
    }
    return grouped;
  }, []);
}

function matchesOptimisticMessage(
  persistedMessage: ChatMessageEvent,
  optimisticMessage: OptimisticChatMessage
): boolean {
  const createdAtDifference = Math.abs(
    persistedMessage.createdAt.getTime() - optimisticMessage.createdAt.getTime()
  );
  if (
    persistedMessage.message !== optimisticMessage.message ||
    createdAtDifference > OPTIMISTIC_MESSAGE_MATCH_WINDOW_MS
  ) {
    return false;
  }
  const persistedAttachmentNames = (persistedMessage.attachments ?? [])
    .map(({ name }) => name)
    .sort();
  const optimisticAttachmentNames = [
    ...optimisticMessage.attachmentNames,
  ].sort();
  return (
    persistedAttachmentNames.length === optimisticAttachmentNames.length &&
    persistedAttachmentNames.every(
      (name, index) => name === optimisticAttachmentNames[index]
    )
  );
}

function isReplyToCurrentConversation(
  event: ActionExecutionEvent,
  conversationId: string
): boolean {
  if (event.name !== ActionExecutionEventType.TOOL_USED) return false;
  if (event.data?.eventDataType !== ActionExecutionEventType.TOOL_USED) {
    return false;
  }
  return isReplyToConversation(event.data, conversationId);
}

function hideRepeatedPlatformsUntilLastMessage(
  items: TranscriptItem[]
): TranscriptItem[] {
  return items.map((item, index) => {
    const nextItem = items[index + 1];
    if (
      item.kind !== 'message' ||
      nextItem?.kind !== 'message' ||
      !hasSameContributor(item.message, nextItem.message)
    ) {
      return item;
    }

    return {
      ...item,
      sourcePlatform:
        item.sourcePlatform === nextItem.sourcePlatform
          ? undefined
          : item.sourcePlatform,
      mirroredPlatforms: item.mirroredPlatforms.filter(
        (platform) => !nextItem.mirroredPlatforms.includes(platform)
      ),
    };
  });
}

function hasSameContributor(
  firstMessage: ChatMessageEvent,
  secondMessage: ChatMessageEvent
): boolean {
  if (firstMessage.role !== secondMessage.role) return false;
  if (firstMessage.sender?.id || secondMessage.sender?.id) {
    return firstMessage.sender?.id === secondMessage.sender?.id;
  }
  return true;
}

function getLatestMessageId(
  messages: ConversationMessageEvent[]
): string | undefined {
  // NOTE(wassim): A JS Date drops the microseconds that order same-millisecond
  // messages, so ties fall back to the list's database order.
  const latest = messages.reduce<ConversationMessageEvent | undefined>(
    (current, message) =>
      !current || message.createdAt >= current.createdAt ? message : current,
    undefined
  );
  return latest?.id;
}

// NOTE(mike): Mirror copies (rows with a canonicalMessageId) are hidden from
// the transcript, so we surface their platforms on the canonical message.
function buildMirroredPlatformsByCanonicalId(
  messages: ConversationMessageEvent[]
): Map<string, ExternalPlatform[]> {
  const platformsByCanonicalId = new Map<string, ExternalPlatform[]>();
  for (const message of messages) {
    if (message.type !== ConversationMessageEventType.CHAT_MESSAGE) continue;
    const { canonicalMessageId, externalPlatform } =
      message as ChatMessageEvent;
    if (isNil(canonicalMessageId) || isNil(externalPlatform)) continue;
    const platforms = platformsByCanonicalId.get(canonicalMessageId) ?? [];
    if (!platforms.includes(externalPlatform)) {
      platforms.push(externalPlatform);
    }
    platformsByCanonicalId.set(canonicalMessageId, platforms);
  }
  return platformsByCanonicalId;
}

function getPlatformDirections(
  message: ChatMessageEvent,
  mirroredPlatformsByCanonicalId: Map<string, ExternalPlatform[]>
): {
  sourcePlatform?: ExternalPlatform;
  mirroredPlatforms: ExternalPlatform[];
} {
  const mirroredPlatforms =
    mirroredPlatformsByCanonicalId.get(message.id) ?? [];
  const externalPlatform = message.externalPlatform;

  if (
    isNil(externalPlatform) ||
    message.role !== ConversationContributionRole.ASSISTANT
  ) {
    return {
      sourcePlatform: externalPlatform,
      mirroredPlatforms,
    };
  }

  return {
    mirroredPlatforms: mirroredPlatforms.includes(externalPlatform)
      ? mirroredPlatforms
      : [...mirroredPlatforms, externalPlatform],
  };
}

function getItemTime(item: TranscriptItem): number {
  return getItemCreatedAt(item).getTime();
}

function getItemCreatedAt(item: TranscriptItem): Date {
  if (item.kind === 'tool-group') return getItemCreatedAt(item.items[0]);
  return item.kind === 'event' ? item.event.createdAt : item.message.createdAt;
}

function toVeeReplyId(item: TranscriptItem): string[] {
  return item.kind === 'message' && isVeeReply(item.message)
    ? [item.message.id]
    : [];
}

function isVeeReply(message: ChatMessageEvent): boolean {
  return (
    message.role === ConversationContributionRole.ASSISTANT &&
    !isMirroredToolCallTranscript(message.message.trim())
  );
}

function getItemKey(item: TranscriptItem): string {
  if (item.kind === 'tool-group') return getItemKey(item.items[0]);
  return item.kind === 'event' ? item.event.id : item.message.id;
}

function isNewLocalDay(
  item: TranscriptItem,
  previous?: TranscriptItem
): boolean {
  if (!previous) return true;
  return (
    getLocalDayKey(getItemCreatedAt(item)) !==
    getLocalDayKey(getItemCreatedAt(previous))
  );
}

function isStampedMessage(item: TranscriptItem): boolean {
  return item.kind === 'message';
}

function getNextStampedCreatedAt(
  items: TranscriptItem[],
  itemIndex: number
): Date | undefined {
  for (let index = itemIndex + 1; index < items.length; index += 1) {
    const item = items[index];
    if (isStampedMessage(item)) {
      return getItemCreatedAt(item);
    }
  }
  return undefined;
}

function isDisplayableChatMessage(
  message: ConversationMessageEvent
): message is ChatMessageEvent {
  if (message.type !== ConversationMessageEventType.CHAT_MESSAGE) return false;
  const chatMessage = message as ChatMessageEvent;
  return (
    [
      ConversationContributionRole.USER,
      ConversationContributionRole.ASSISTANT,
    ].includes(chatMessage.role) && isNil(chatMessage.canonicalMessageId)
  );
}

type ChatMessageProps = {
  isLatestVeeReply?: boolean;
  isRatingLoading?: boolean;
  isTimestampCollapsed?: boolean;
  isTimestampRevealed?: boolean;
  message: ChatMessageEvent;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  rating?: MessageRatingFragment;
  sourcePlatform?: ExternalPlatform;
  mirroredPlatforms: ExternalPlatform[];
};

function shouldIgnoreTimestampToggleClick(
  event: MouseEvent<HTMLElement>
): boolean {
  if (!(event.target instanceof Element)) {
    return true;
  }
  if (
    event.target.closest(
      'a, button, input, textarea, select, [role="button"], [role="link"]'
    )
  ) {
    return true;
  }
  const selection = window.getSelection();
  return Boolean(
    selection &&
      selection.toString() &&
      event.currentTarget.contains(selection.anchorNode)
  );
}

function ChatMessage({
  isLatestVeeReply = false,
  isRatingLoading = false,
  isTimestampCollapsed = false,
  message,
  rating,
  sourcePlatform,
  mirroredPlatforms,
}: ChatMessageProps) {
  const [isTimestampRevealed, setIsTimestampRevealed] = useState(false);
  const isHuman = message.role === ConversationContributionRole.USER;

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    if (shouldIgnoreTimestampToggleClick(event)) {
      return;
    }
    setIsTimestampRevealed((isRevealed) => !isRevealed);
  }

  return isHuman ? (
    <HumanMessage
      isTimestampCollapsed={isTimestampCollapsed}
      isTimestampRevealed={isTimestampRevealed}
      message={message}
      onClick={isTimestampCollapsed ? handleClick : undefined}
      sourcePlatform={sourcePlatform}
      mirroredPlatforms={mirroredPlatforms}
    />
  ) : (
    <AssistantMessage
      isLatestVeeReply={isLatestVeeReply}
      isRatingLoading={isRatingLoading}
      isTimestampCollapsed={isTimestampCollapsed}
      isTimestampRevealed={isTimestampRevealed}
      message={message}
      onClick={isTimestampCollapsed ? handleClick : undefined}
      rating={rating}
      sourcePlatform={sourcePlatform}
      mirroredPlatforms={mirroredPlatforms}
    />
  );
}

function HumanMessage({
  isTimestampCollapsed = false,
  isTimestampRevealed = false,
  message,
  onClick,
  sourcePlatform,
  mirroredPlatforms,
}: ChatMessageProps) {
  const senderName = message.sender
    ? message.sender.name || toName(message.sender) || message.sender.username
    : undefined;
  const hasMessageText = Boolean(message.message.trim());
  return (
    <div
      className={`flex items-start justify-end gap-2 pl-24 ${
        isTimestampCollapsed ? 'cursor-pointer' : ''
      }`}
      data-chat-message
      onClick={onClick}
    >
      <div className="flex min-w-0 flex-col items-end gap-1">
        {hasMessageText && (
          <div className="max-w-full rounded-2xl bg-gray-100 px-4 py-2.5">
            <MessageText message={message} className="text-gray-800" />
          </div>
        )}
        <MessageFooter
          isTimestampCollapsed={isTimestampCollapsed}
          isTimestampRevealed={isTimestampRevealed}
          message={message}
          sourcePlatform={sourcePlatform}
          mirroredPlatforms={mirroredPlatforms}
        />
      </div>
      <div className="mt-1.5 shrink-0">
        <Avatar
          size="medium"
          user={{
            id: message.sender?.id ?? message.id,
            name: senderName,
            roles: [],
          }}
          tooltipContent={senderName}
        />
      </div>
    </div>
  );
}

function OptimisticHumanMessage({
  message,
  user,
}: {
  message: OptimisticChatMessage;
  user?: User | null;
}) {
  const hasMessageText = Boolean(message.message.trim());
  const isSending = message.status === 'sending';
  return (
    <div
      className={`flex items-start justify-end gap-2 pl-24 ${
        isSending ? 'opacity-60' : ''
      }`}
    >
      <div className="flex min-w-0 flex-col items-end gap-1">
        {hasMessageText && (
          <div className="max-w-full rounded-2xl bg-gray-100 px-4 py-2.5">
            <MessageMarkdown
              message={message.message}
              className="text-gray-800"
            />
          </div>
        )}
        {message.attachmentNames.map((attachmentName, index) => (
          <Text
            key={`${attachmentName}-${index}`}
            type="body-xs"
            className="rounded-lg bg-gray-100 px-3 py-2 text-gray-600"
          >
            {attachmentName}
          </Text>
        ))}
        {isSending && (
          <Text type="body-xs" className="text-gray-400">
            Sending…
          </Text>
        )}
        {message.status === 'failed' && (
          <div className="flex items-center gap-1" role="alert">
            <Text type="body-xs" className="text-danger-500">
              Failed to send
            </Text>
            <Button isGhost size="xs" variant="danger" onPress={message.retry}>
              Retry
            </Button>
          </div>
        )}
      </div>
      <div className="mt-1.5 shrink-0">
        <Avatar
          size="medium"
          user={user ?? { id: message.id, roles: [] }}
          tooltipContent={user?.name}
        />
      </div>
    </div>
  );
}

function AgentUnavailableNotice() {
  return (
    <div className={`${UNAVAILABLE_NOTICE_ROOT} mt-6 pr-12`} role="status">
      <div className="border-warning-200 bg-warning-50 flex w-fit items-start gap-2 rounded-lg border px-3 py-2">
        <PiWarningCircle
          aria-hidden
          className="text-warning-500 mt-0.5 h-4 w-4 flex-shrink-0"
        />
        <Text type="body-sm" className="text-dark">
          {AGENT_UNAVAILABLE_MESSAGE}
        </Text>
      </div>
    </div>
  );
}

function AgentWorkingIndicator() {
  return (
    <div
      aria-label="Vooma is working"
      className="mt-6 flex items-center gap-1 py-2"
      role="status"
    >
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          aria-hidden
          className="h-2 w-2 animate-bounce rounded-full bg-gray-400 motion-reduce:animate-none"
          style={{ animationDelay: `${index * 150}ms` }}
        />
      ))}
    </div>
  );
}

function AssistantMessage({
  isLatestVeeReply = false,
  isRatingLoading = false,
  isTimestampCollapsed = false,
  isTimestampRevealed = false,
  message,
  onClick,
  rating,
  sourcePlatform,
  mirroredPlatforms,
}: ChatMessageProps) {
  return (
    <div
      className={`group flex flex-col items-start gap-1 pr-12 ${
        isTimestampCollapsed ? 'cursor-pointer' : ''
      }`}
      data-chat-message
      onClick={onClick}
    >
      {message.card && !isQuickReplyCard(message.card) ? (
        <InteractiveCard card={message.card} />
      ) : (
        <MessageText message={message} className="text-dark" />
      )}
      {message.card && isQuickReplyCard(message.card) && (
        <QuickReplyButtons
          card={message.card}
          conversationId={message.conversationId}
        />
      )}
      <MessageFooter
        isTimestampCollapsed={isTimestampCollapsed}
        isTimestampRevealed={isTimestampRevealed}
        message={message}
        sourcePlatform={sourcePlatform}
        mirroredPlatforms={mirroredPlatforms}
        timestampAccessory={
          isVeeReply(message) ? (
            <MessageFeedback
              messageId={message.id}
              conversationId={message.conversationId}
              rating={rating}
              isAlwaysVisible={isLatestVeeReply}
              isDisabled={isRatingLoading}
            />
          ) : undefined
        }
      />
    </div>
  );
}

function MessageText({
  message,
  className,
}: {
  message: ChatMessageEvent;
  className?: string;
}) {
  return (
    <MessageMarkdown
      message={convertSlackMrkdwnLinks(message.message)}
      className={className}
    />
  );
}

function MessageTimestamp({
  createdAt,
  isCollapsed,
  isRevealed,
  isInRow = false,
}: {
  createdAt: Date;
  isCollapsed: boolean;
  isRevealed: boolean;
  isInRow?: boolean;
}) {
  const isHidden = isCollapsed && !isRevealed;
  return (
    <div
      className={`overflow-hidden whitespace-nowrap transition-[max-height,opacity] duration-150 ease-out motion-reduce:transition-none ${
        isHidden ? 'max-h-0 max-w-0 opacity-0' : 'max-h-5 opacity-100'
      }`}
    >
      <Tooltip content={formatFullDatetime(createdAt)}>
        {/* NOTE(aman): flex drops the inherited 24px line box, which sits the
            12px text below the center of the row it shares. */}
        <time
          className={isInRow ? 'flex' : undefined}
          data-timestamp-collapsed={isCollapsed}
          dateTime={createdAt.toISOString()}
        >
          <Text type="body-xs" as="span" className="text-gray-400">
            {formatChatMessageTimestamp(createdAt)}
          </Text>
        </time>
      </Tooltip>
    </div>
  );
}

function MessageFooter({
  isTimestampCollapsed = false,
  isTimestampRevealed = false,
  message,
  sourcePlatform,
  mirroredPlatforms,
  timestampAccessory,
}: ChatMessageProps & { timestampAccessory?: ReactNode }) {
  const { isWeb } = usePlatform();
  const isTimestampHidden = isTimestampCollapsed && !isTimestampRevealed;
  const timestamp = (
    <MessageTimestamp
      createdAt={message.createdAt}
      isCollapsed={isTimestampCollapsed}
      isRevealed={isTimestampRevealed}
      isInRow={Boolean(timestampAccessory)}
    />
  );
  const platformIndicators = [
    ...(sourcePlatform
      ? [{ platform: sourcePlatform, label: 'via' as const }]
      : []),
    ...mirroredPlatforms.map((platform) => ({
      platform,
      label: 'mirrored to' as const,
    })),
  ];
  return (
    <>
      {timestampAccessory ? (
        <div
          className={`flex items-center ${isTimestampHidden ? '' : 'gap-1'}`}
        >
          {timestampAccessory}
          {timestamp}
        </div>
      ) : (
        timestamp
      )}
      {message.attachments && message.attachments.length > 0 && (
        <ChatAttachments
          alignment={
            message.role === ConversationContributionRole.USER ? 'end' : 'start'
          }
          attachments={message.attachments}
          enableImagePreview={isWeb}
          renderDocumentPreview={isWeb ? renderDocumentPreview : undefined}
        />
      )}
      {platformIndicators.length > 0 && (
        <div className="flex items-center gap-2">
          {platformIndicators.map(({ platform, label }) => (
            <div
              key={`${label}-${platform}`}
              className="flex items-center gap-1"
            >
              <span aria-hidden>
                <ExternalPlatformIcon platform={platform} size={12} />
              </span>
              <Text type="body-xs" className="text-gray-400">
                {label} {getExternalPlatformLabel(platform)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
